import React, { useState, Component } from 'react';
import { supabase } from '../../lib/supabase';
import { FileJson, Store, CheckCircle2, AlertCircle, Loader2, Database, Layers, UtensilsCrossed, Settings2, ChefHat, Link2, MessageCircle, ArrowRight } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────
interface RestaurantPayload {
  name: string;
  slug: string;
  whatsapp_number?: string;
  [key: string]: any;
}
interface ParsedPayload {
  restaurant: RestaurantPayload;
  categories: any[];
  items: any[];
  options: any[];
  stations: any[];
}
interface CreateResult {
  success: boolean;
  restaurant_id?: number;
  restaurant_slug?: string;
  menu_import?: {
    categories_created_or_updated: number;
    items_created_or_updated: number;
    options_inserted: number;
  };
  stations_created?: number;
  station_mappings_created?: number;
  [key: string]: any;
}
interface ValidationState {
  status: 'idle' | 'valid' | 'error';
  errors: string[];
  counts: {
    categories: number;
    items: number;
    options: number;
    stations: number;
  };
}
// ─── Summary Card ────────────────────────────────────────────
function SummaryMetric({
  icon: Icon,
  label,
  value
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
}) {
  return <div className="bg-[#0B0B0B] border border-white/5 rounded-xl p-4 text-center" data-id="element-1895">
      <Icon className="w-4 h-4 text-white/30 mx-auto mb-2" data-id="element-1896" />
      <div className="text-2xl font-bold text-white" data-id="element-1897">{value}</div>
      <div className="text-[10px] text-white/40 uppercase tracking-widest mt-1" data-id="element-1898">
        {label}
      </div>
    </div>;
}
// ─── Page Component ──────────────────────────────────────────
export function CreateRestaurantPage() {
  const [jsonInput, setJsonInput] = useState('');
  const [parsedData, setParsedData] = useState<ParsedPayload | null>(null);
  const [validation, setValidation] = useState<ValidationState>({
    status: 'idle',
    errors: [],
    counts: {
      categories: 0,
      items: 0,
      options: 0,
      stations: 0
    }
  });
  const [restaurantFields, setRestaurantFields] = useState({
    name: '',
    slug: '',
    whatsapp_number: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createResult, setCreateResult] = useState<CreateResult | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  // ─── Validate ────────────────────────────────────────────
  const handleValidate = () => {
    setParsedData(null);
    setCreateResult(null);
    setCreateError(null);
    const errors: string[] = [];
    if (!jsonInput.trim()) {
      setValidation({
        status: 'error',
        errors: ['El JSON está vacío.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    let data: any;
    try {
      data = JSON.parse(jsonInput);
    } catch {
      setValidation({
        status: 'error',
        errors: ['El JSON no es válido. Revisa la sintaxis.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    // Structural validation
    if (!data.restaurant || typeof data.restaurant !== 'object' || Array.isArray(data.restaurant)) {
      errors.push('"restaurant" debe ser un objeto.');
    }
    if (!Array.isArray(data.categories)) {
      errors.push('"categories" debe ser un array.');
    }
    if (!Array.isArray(data.items)) {
      errors.push('"items" debe ser un array.');
    }
    if (!Array.isArray(data.options)) {
      errors.push('"options" debe ser un array.');
    }
    if (!Array.isArray(data.stations)) {
      errors.push('"stations" debe ser un array.');
    }
    if (errors.length > 0) {
      setValidation({
        status: 'error',
        errors,
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    const counts = {
      categories: data.categories.length,
      items: data.items.length,
      options: data.options.length,
      stations: data.stations.length
    };
    setParsedData(data as ParsedPayload);
    setRestaurantFields({
      name: data.restaurant.name || '',
      slug: data.restaurant.slug || '',
      whatsapp_number: data.restaurant.whatsapp_number || ''
    });
    setValidation({
      status: 'valid',
      errors: [],
      counts
    });
  };
  // ─── Create ──────────────────────────────────────────────
  const handleCreate = async () => {
    if (!parsedData) return;
    // Re-validate
    if (validation.status !== 'valid') {
      handleValidate();
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    setCreateResult(null);
    try {
      const payloadToSend: ParsedPayload = {
        ...parsedData,
        restaurant: {
          ...parsedData.restaurant,
          name: restaurantFields.name,
          slug: restaurantFields.slug,
          whatsapp_number: restaurantFields.whatsapp_number
        }
      };
      const {
        data,
        error
      } = await supabase.rpc('create_restaurant_from_menu_json', {
        p_payload: payloadToSend
      });
      if (error) throw error;
      setCreateResult(data as CreateResult);
    } catch (err: any) {
      console.error('[CreateRestaurant] RPC error:', err);
      setCreateError(err.message || 'Error desconocido al crear el restaurante.');
    } finally {
      setIsCreating(false);
    }
  };
  // ─── Render ──────────────────────────────────────────────
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans selection:bg-[#006847] selection:text-white" data-id="element-1899">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-8" data-id="element-1900">
        {/* Header */}
        <header className="flex items-start gap-4" data-id="element-1901">
          <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center shrink-0" data-id="element-1902">
            <Database className="w-7 h-7 text-[#006847]" data-id="element-1903" />
          </div>
          <div data-id="element-1904">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-id="element-1905">
              Crear restaurante desde menú
            </h1>
            <p className="text-white/40 text-sm mt-1 max-w-xl" data-id="element-1906">
              Pega el JSON generado por el parser para crear restaurante, menú y
              KDS automáticamente.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1907">
          {/* ─── Left: JSON Input ─── */}
          <div className="lg:col-span-7 space-y-6" data-id="element-1908">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/20" data-id="element-1909">
              <div className="flex items-center justify-between mb-4" data-id="element-1910">
                <h2 className="text-base font-semibold flex items-center gap-2" data-id="element-1911">
                  <FileJson className="w-5 h-5 text-white/40" data-id="element-1912" />
                  Payload JSON
                </h2>
                <button onClick={handleValidate} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition-all active:scale-95" data-id="element-1913">
                  Validar JSON
                </button>
              </div>

              <textarea value={jsonInput} onChange={e => setJsonInput(e.target.value)} placeholder="Pega aquí el JSON generado por el sistema..." className="w-full h-[420px] bg-[#0B0B0B] border border-white/10 rounded-xl p-4 font-mono text-[13px] leading-relaxed text-white/80 placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 resize-y transition-colors" spellCheck={false} data-id="element-1914" />

              {/* Validation Error */}
              {validation.status === 'error' && <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-1915">
                  <div className="flex items-center gap-2 text-red-400 mb-2" data-id="element-1916">
                    <AlertCircle className="w-4 h-4" data-id="element-1917" />
                    <strong className="text-sm font-semibold" data-id="element-1918">
                      Errores de validación
                    </strong>
                  </div>
                  <ul className="space-y-1 text-sm text-red-300/80 ml-6 list-disc" data-id="element-1919">
                    {validation.errors.map((err, i) => <li key={i} data-id="element-1920">{err}</li>)}
                  </ul>
                </div>}

              {/* Validation Success */}
              {validation.status === 'valid' && <div className="mt-4 p-4 bg-[#006847]/10 border border-[#006847]/20 rounded-xl" data-id="element-1921">
                  <div className="flex items-center gap-2 text-[#00a870] mb-4" data-id="element-1922">
                    <CheckCircle2 className="w-4 h-4" data-id="element-1923" />
                    <strong className="text-sm font-semibold" data-id="element-1924">
                      JSON válido — listo para crear
                    </strong>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-1925">
                    <SummaryMetric icon={Layers} label="Categorías" value={validation.counts.categories} data-id="element-1926" />
                    <SummaryMetric icon={UtensilsCrossed} label="Items" value={validation.counts.items} data-id="element-1927" />
                    <SummaryMetric icon={Settings2} label="Opciones" value={validation.counts.options} data-id="element-1928" />
                    <SummaryMetric icon={ChefHat} label="Estaciones" value={validation.counts.stations} data-id="element-1929" />
                  </div>
                </div>}
            </div>
          </div>

          {/* ─── Right: Preview & Actions ─── */}
          <div className="lg:col-span-5 space-y-6" data-id="element-1930">
            {/* Restaurant Preview */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/20 sticky top-6" data-id="element-1931">
              <h2 className="text-base font-semibold flex items-center gap-2 mb-6" data-id="element-1932">
                <Store className="w-5 h-5 text-white/40" data-id="element-1933" />
                Datos del Restaurante
              </h2>

              <div className="space-y-4" data-id="element-1934">
                <div data-id="element-1935">
                  <label className="block text-[11px] font-medium text-white/40 mb-1.5 uppercase tracking-widest" data-id="element-1936">
                    Nombre
                  </label>
                  <div className="relative" data-id="element-1937">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-1938" />
                    <input type="text" value={restaurantFields.name} onChange={e => setRestaurantFields({
                    ...restaurantFields,
                    name: e.target.value
                  })} disabled={!parsedData || isCreating} placeholder="Ej. Mi Restaurante" className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-1939" />
                  </div>
                </div>

                <div data-id="element-1940">
                  <label className="block text-[11px] font-medium text-white/40 mb-1.5 uppercase tracking-widest" data-id="element-1941">
                    Slug (URL)
                  </label>
                  <div className="relative" data-id="element-1942">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-1943" />
                    <input type="text" value={restaurantFields.slug} onChange={e => setRestaurantFields({
                    ...restaurantFields,
                    slug: e.target.value
                  })} disabled={!parsedData || isCreating} placeholder="mi-restaurante" className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-1944" />
                  </div>
                </div>

                <div data-id="element-1945">
                  <label className="block text-[11px] font-medium text-white/40 mb-1.5 uppercase tracking-widest" data-id="element-1946">
                    WhatsApp
                  </label>
                  <div className="relative" data-id="element-1947">
                    <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-1948" />
                    <input type="text" value={restaurantFields.whatsapp_number} onChange={e => setRestaurantFields({
                    ...restaurantFields,
                    whatsapp_number: e.target.value
                  })} disabled={!parsedData || isCreating} placeholder="50688888888" className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-1949" />
                  </div>
                </div>
              </div>

              {/* Create Button */}
              <div className="mt-8 pt-6 border-t border-white/10" data-id="element-1950">
                <button onClick={handleCreate} disabled={!parsedData || isCreating || validation.status !== 'valid'} className="w-full flex items-center justify-center gap-2.5 bg-[#006847] hover:bg-[#005a3d] active:scale-[0.98] disabled:bg-white/5 disabled:text-white/25 text-white font-bold py-3.5 px-4 rounded-xl transition-all" data-id="element-1951">
                  {isCreating ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-1952" />
                      Creando restaurante...
                    </> : <>
                      <Database className="w-5 h-5" data-id="element-1953" />
                      Crear Restaurante
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-1954" />
                    </>}
                </button>
              </div>

              {/* Error State */}
              {createError && <div className="mt-5 p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-1955">
                  <div className="flex items-start gap-3 text-red-400" data-id="element-1956">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-1957" />
                    <div className="text-sm" data-id="element-1958">
                      <strong className="block font-semibold mb-1" data-id="element-1959">
                        Error al crear
                      </strong>
                      <p className="text-red-300/80" data-id="element-1960">{createError}</p>
                    </div>
                  </div>
                </div>}

              {/* Success State */}
              {createResult && !createError && <div className="mt-5 p-5 bg-[#006847]/10 border border-[#006847]/30 rounded-xl" data-id="element-1961">
                  <div className="flex items-center gap-3 mb-5" data-id="element-1962">
                    <div className="w-10 h-10 bg-[#006847]/20 rounded-full flex items-center justify-center shrink-0" data-id="element-1963">
                      <CheckCircle2 className="w-5 h-5 text-[#00a870]" data-id="element-1964" />
                    </div>
                    <div data-id="element-1965">
                      <strong className="block text-sm font-bold text-white" data-id="element-1966">
                        ¡Restaurante creado!
                      </strong>
                      <span className="text-xs text-[#00a870]" data-id="element-1967">
                        Todos los datos fueron insertados correctamente.
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm bg-[#0B0B0B] p-4 rounded-xl border border-white/5" data-id="element-1968">
                    <ResultRow label="ID Restaurante" value={createResult.restaurant_id} data-id="element-1969" />
                    <ResultRow label="Slug" value={createResult.restaurant_slug || restaurantFields.slug} data-id="element-1970" />
                    <div className="border-t border-white/5 my-2" data-id="element-1971" />
                    <ResultRow label="Categorías creadas" value={createResult.menu_import?.categories_created_or_updated} data-id="element-1972" />
                    <ResultRow label="Items creados" value={createResult.menu_import?.items_created_or_updated} data-id="element-1973" />
                    <ResultRow label="Opciones insertadas" value={createResult.menu_import?.options_inserted} data-id="element-1974" />
                    <div className="border-t border-white/5 my-2" data-id="element-1975" />
                    <ResultRow label="Estaciones creadas" value={createResult.stations_created} data-id="element-1976" />
                    <ResultRow label="Mapeos de estación" value={createResult.station_mappings_created} data-id="element-1977" />
                  </div>

                  {createResult.restaurant_slug && <div className="mt-4 text-center" data-id="element-1978">
                      <a href={`/r/${createResult.restaurant_slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#00a870] hover:text-white transition-colors font-medium" data-id="element-1979">
                        Ver menú público
                        <ArrowRight className="w-4 h-4" data-id="element-1980" />
                      </a>
                    </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ─── Result Row Helper ───────────────────────────────────────
function ResultRow({
  label,
  value
}: {
  label: string;
  value: any;
}) {
  return <div className="flex justify-between items-center" data-id="element-1981">
      <span className="text-white/50" data-id="element-1982">{label}</span>
      <strong className="text-white font-mono text-xs" data-id="element-1983">{value ?? '—'}</strong>
    </div>;
}